/**
 * ORBITVU 360 PRODUCT GALLERY INTEGRATION
 * 
 * This script handles the integration of Orbitvu 360-degree product viewers
 * with the Shopify product gallery. It manages:
 * - Dynamic loading of 360 viewers based on variant selection
 * - Correct carousel image selection when Orbitvu is present
 * - Thumbnail updates and UI synchronization
 */

// Store variant metafields from the global window object, or an empty array if not present
const variantMetafields = window.variantMetafields || [];

// Check if Orbitvu 360 is enabled via section settings
const isOrbitvuEnabled = () => {
  // Check if the setting is available in the DOM
  const productGallery = document.querySelector('product-gallery');
  if (productGallery) {
    // Look for a data attribute or check if Orbitvu elements exist
    const orbitvuContainer = document.getElementById('orbitvu-media-container');
    return orbitvuContainer !== null;
  }
  return false;
};

/**
 * Handle variant change events from Shopify
 * This is triggered when a customer selects a different product variant
 */
document.addEventListener('variant:change', (event) => {
  const variant = event.detail.variant;
  if (!variant) return; // Exit if no variant is provided
  
  // Exit if Orbitvu 360 is not enabled
  if (!isOrbitvuEnabled()) return;

  // Find the Orbitvu metafield data for the selected variant
  const metafield = (window.variantMetafields || []).find((m) => m.id === variant.id);
  const orbitvuUid = metafield ? metafield.orbitvu_uid : null;
  const orbitvuDirectLink = metafield ? metafield.orbitvu_directlink : null;

  // Get DOM elements for Orbitvu media container and thumbnail
  const orbitvuMediaContainer = document.getElementById('orbitvu-media-container');
  const orbitvuThumbnail = document.getElementById('orbitvu-thumbnail');

  if (!orbitvuMediaContainer) return; // Exit if Orbitvu container is missing

  /**
   * CASE 1: Variant has Orbitvu 360 data available
   * Show the Orbitvu viewer and handle carousel selection
   */
  if (orbitvuUid && orbitvuDirectLink) {
    // Show Orbitvu media container and thumbnail
    orbitvuMediaContainer.hidden = false;
    if (orbitvuThumbnail) {
      orbitvuThumbnail.hidden = false;
      
      // Update the Orbitvu thumbnail to show the variant's image
      updateOrbitvuThumbnailImage(variant);
    }

    /**
     * Update Orbitvu viewer if the UID has changed
     * This prevents unnecessary script reloading for the same 360 view
     */
    const orbitvuViewerDiv = orbitvuMediaContainer.querySelector('.orbitvu-viewer > div');
    const oldUid = orbitvuMediaContainer.dataset.mediaId;

    if (oldUid !== orbitvuUid) {
      // Update the viewer div's ID and data attribute
      orbitvuViewerDiv.id = `ovContent-${orbitvuUid}`;
      orbitvuMediaContainer.dataset.mediaId = orbitvuUid;
      orbitvuViewerDiv.innerHTML = '';

      // Remove any existing Orbitvu script to prevent conflicts
      let orbitvuScript = document.getElementById('orbitvu-script');
      if (orbitvuScript) {
        orbitvuScript.remove();
      }

      // Create and load new Orbitvu script for the selected variant
      const newScript = document.createElement('script');
      newScript.id = 'orbitvu-script';
      newScript.type = 'text/javascript';
      newScript.async = true;
      // Build the script source URL from the direct link
      const scriptSrc = orbitvuDirectLink.replace('/view', '') + '/script?width=auto&height=auto&content2=yes&partial_load=yes';
      newScript.src = scriptSrc;
      document.body.appendChild(newScript);
    }

    /**
     * Handle carousel image selection when Orbitvu is available
     * Since Orbitvu is at position 0, we need to find the correct variant image
     * and ensure it's selected without any flash of wrong images
     */
    if (variant.featured_media) {
      // Get all visible media items in the carousel to understand the structure
      const carousels = document.querySelectorAll('media-carousel');
      const carousel = carousels[0]; // Use the first carousel
      
      if (carousel) {
        // Get all visible media items (excluding hidden ones)
        const visibleMediaItems = Array.from(carousel.querySelectorAll('.product-gallery__media:not([hidden])'));
        
        // Find the media item that matches the variant's featured media by ID
        const targetMediaId = variant.featured_media.id;
        
        const targetIndex = visibleMediaItems.findIndex(item => {
          const itemId = item.getAttribute('data-media-id');
          return itemId === targetMediaId.toString();
        });
        
        if (targetIndex !== -1) {
          /**
           * Primary method: Select by media ID match
           * This is the most reliable method as it finds the exact media item
           */
          carousels.forEach(carousel => {
            carousel.select(targetIndex, { animate: false });
          });
          
          // Update UI elements to reflect the correct selection
          updateAriaCurrentAttributes(targetIndex);
          
          // Double-check and correct after a short delay to ensure it sticks
          // This prevents the theme's default behavior from overriding our selection
          setTimeout(() => {
            carousels.forEach(carousel => {
              carousel.select(targetIndex, { animate: false });
            });
            updateAriaCurrentAttributes(targetIndex);
          }, 100);
        } else {
          /**
           * Fallback method: Position-based selection
           * Since Orbitvu is at position 0, the carousel indices are shifted by 1
           */
          const fallbackIndex = variant.featured_media.position;
          carousels.forEach(carousel => {
            carousel.select(fallbackIndex, { animate: false });
          });
          updateAriaCurrentAttributes(fallbackIndex);
        }
      }
    }
  } else {
    /**
     * CASE 2: Variant does not have Orbitvu 360 data
     * Keep 360 image visible using first available 360 data, handle carousel selection
     */
    
    // Keep Orbitvu media container visible - 360 should always be available if any variant has it
    orbitvuMediaContainer.hidden = false;
    if (orbitvuThumbnail) {
      orbitvuThumbnail.hidden = false;
    }

    /**
     * Handle carousel selection when Orbitvu is available but current variant doesn't have 360 data
     * Since Orbitvu is at position 0, the carousel indices are shifted by 1
     */
    if (variant.featured_media) {
      setTimeout(() => {
        const carousels = document.querySelectorAll('media-carousel');
        let adjustedIndex;
        carousels.forEach(carousel => {
          // Since Orbitvu is at position 0, use position-based selection with shift
          adjustedIndex = variant.featured_media.position;
          carousel.select(adjustedIndex, { animate: false });
        });
        
        // Update UI elements to reflect the correct selection
        updateAriaCurrentAttributes(adjustedIndex);
      }, 10);
    }
  }
});

/**
 * Update the Orbitvu thumbnail image to show the variant's featured image
 * This makes the 360 thumbnail more informative by showing the actual product image
 * 
 * @param {Object} variant - The selected variant object
 */
const updateOrbitvuThumbnailImage = (variant) => {
  const orbitvuThumbnail = document.getElementById('orbitvu-thumbnail');
  if (!orbitvuThumbnail) return;

  // Get the variant's featured media or first available image
  let variantImage = null;
  
  if (variant.featured_media && variant.featured_media.media_type === 'image') {
    // Use the variant's featured media if it's an image
    variantImage = variant.featured_media;
  } else if (variant.featured_image) {
    // Fallback to featured_image if featured_media is not available
    variantImage = {
      preview_image: variant.featured_image,
      media_type: 'image'
    };
  }

  if (variantImage) {
    // Clear existing content and create new thumbnail with variant image
    orbitvuThumbnail.innerHTML = '';
    
    // Create the main product image element
    const img = document.createElement('img');
    img.src = variantImage.preview_image.src;
    img.alt = 'Katso 360-esitys';
    img.className = 'object-contain rounded-sm';
    img.loading = 'lazy';
    img.sizes = '(max-width: 699px) 56px, 64px';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'contain';
    
    // Create the 360-degree badge overlay
    const badge = document.createElement('div');
    badge.className = 'product-gallery__media-badge';
    
    const badgeImg = document.createElement('img');
    badgeImg.src = 'https://cdn.shopify.com/s/files/1/0692/9943/7824/files/icon_360.svg?v=1749126836';
    badgeImg.alt = 'Katso 360-esitys';
    badgeImg.width = 16;
    badgeImg.height = 16;
    badgeImg.style.width = '100%';
    badgeImg.style.height = '100%';
    badgeImg.style.objectFit = 'contain';
    
    badge.appendChild(badgeImg);
    
    // Append both the product image and 360 badge to the thumbnail
    orbitvuThumbnail.appendChild(img);
    orbitvuThumbnail.appendChild(badge);
  } else {
    // Fallback: Show generic 360 icon if no variant image is available
    orbitvuThumbnail.innerHTML = `
      <img src="https://cdn.shopify.com/s/files/1/0692/9943/7824/files/icon_360.svg?v=1749126836" alt="Katso 360-esitys" width="60" height="60" style="width: 100%; height: 100%; object-fit: contain; border-radius: inherit;">
      <div class="product-gallery__media-badge">
        <img src="https://cdn.shopify.com/s/files/1/0692/9943/7824/files/icon_360.svg?v=1749126836" alt="Katso 360-esitys" width="16" height: 16" style="width: 100%; height: 100%; object-fit: contain;">
      </div>
    `;
  }
}

/**
 * Update aria-current attributes for page dots and thumbnails
 * This ensures the UI correctly reflects which image is currently selected
 * 
 * @param {number} selectedIndex - The index of the currently selected image
 */
const updateAriaCurrentAttributes = (selectedIndex) => {
  // Update page dots to show the correct active state
  const pageDots = document.querySelectorAll('.page-dots button');
  pageDots.forEach((dot, index) => {
    dot.setAttribute('aria-current', index === selectedIndex ? 'true' : 'false');
  });
  
  // Update thumbnails to show the correct active state
  const thumbnails = document.querySelectorAll('.product-gallery__thumbnail');
  thumbnails.forEach((thumbnail, index) => {
    thumbnail.setAttribute('aria-current', index === selectedIndex ? 'true' : 'false');
  });
}



/**
 * Initialize Orbitvu integration when the DOM is loaded
 * This handles zoom button visibility and thumbnail interactions
 */
document.addEventListener('DOMContentLoaded', () => {
  // Exit if Orbitvu 360 is not enabled
  if (!isOrbitvuEnabled()) return;
  
  // Get the Orbitvu thumbnail and all zoom buttons
  const orbitvuThumb = document.getElementById('orbitvu-thumbnail');
  const zoomButtons = document.querySelectorAll('.product-gallery__zoom');

  /**
   * Hide zoom buttons when Orbitvu is selected, otherwise show them
   * This prevents zoom functionality from interfering with 360-degree viewing
   */
  const hideZoomIfOrbitvuSelected = () => {
    if (orbitvuThumb && orbitvuThumb.getAttribute('aria-current') === 'true') {
      // Hide zoom buttons when Orbitvu is selected
      zoomButtons.forEach((btn) => { btn.style.display = 'none'; });
    } else {
      // Show zoom buttons when regular images are selected
      zoomButtons.forEach((btn) => { btn.style.display = ''; });
    }
  };

  // Add click listeners to all product gallery thumbnails to update zoom button visibility
  document.querySelectorAll('.product-gallery__thumbnail').forEach((btn) => {
    btn.addEventListener('click', () => {
      // Delay to allow aria-current attribute to update before checking
      setTimeout(hideZoomIfOrbitvuSelected, 10);
    });
  });

  // Initial check on page load to set correct zoom button visibility
  hideZoomIfOrbitvuSelected();

  // Observe changes to the Orbitvu thumbnail's aria-current attribute
  // This ensures zoom buttons are hidden/shown when Orbitvu selection changes
  if (orbitvuThumb) {
    const observer = new MutationObserver(hideZoomIfOrbitvuSelected);
    observer.observe(orbitvuThumb, { attributes: true, attributeFilter: ['aria-current'] });
  }
});